import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

type PurchaseLogStatus = 'attempted' | 'success' | 'failed' | 'canceled' | 'unknown';

type PurchaseLog = {
  id: string;
  app?: string;
  platform?: string;
  purchaseStatus?: string;
  status?: string;
  userId?: string;
  email?: string;
  plan?: Record<string, unknown> | string | null;
  source?: string;
  errorDomain?: string;
  errorCode?: string | number;
  readableErrorCode?: string | number;
  errorDescription?: string;
  failureReason?: string;
  cancelReasonCode?: string;
  cancelReasonLabel?: string;
  cancelFeedbackTrigger?: string;
  metadata?: Record<string, unknown> | null;
  cancelFeedbackMetadata?: Record<string, unknown> | null;
  createdAt?: Timestamp | Date | number | string | null;
  createdAtEpoch?: number | string | null;
  updatedAt?: Timestamp | Date | number | string | null;
};

const COLLECTION_NAME = 'Parrot-purchase-logs';

const toDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const date = new Date(value < 1_000_000_000_000 ? value * 1000 : value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric) && value.trim() !== '') return toDate(numeric);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  return null;
};

const formatTimestamp = (...values: unknown[]) => {
  for (const value of values) {
    const date = toDate(value);
    if (date) return date.toLocaleString();
  }
  return 'N/A';
};

const valueText = (value: unknown) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
};

const planObject = (log: PurchaseLog): Record<string, unknown> => {
  if (!log.plan || typeof log.plan !== 'object') return {};
  return log.plan;
};

const planLabel = (log: PurchaseLog) => {
  if (typeof log.plan === 'string' && log.plan.trim()) return log.plan;
  const plan = planObject(log);
  return valueText(
    plan.productIdentifier ||
      plan.productId ||
      plan.packageIdentifier ||
      plan.selectedPlanID ||
      plan.id ||
      plan.planPeriod ||
      plan.displayName ||
      plan.name
  );
};

const planDetail = (log: PurchaseLog) => {
  const plan = planObject(log);
  const price = valueText(plan.localizedPrice || plan.price);
  const period = valueText(plan.subscriptionPeriod || plan.planPeriod || plan.periodUnit);
  const packageID = valueText(plan.packageIdentifier);
  const productID = valueText(plan.productIdentifier || plan.productId);
  return [
    price !== 'N/A' ? price : null,
    period !== 'N/A' ? period : null,
    productID !== 'N/A' ? productID : null,
    packageID !== 'N/A' ? packageID : null,
  ]
    .filter(Boolean)
    .join(' / ') || 'Plan details unavailable';
};

const normalizeStatus = (log: PurchaseLog): PurchaseLogStatus => {
  const raw = String(log.purchaseStatus || log.status || '').toLowerCase();
  if (raw === 'attempted' || raw === 'success' || raw === 'failed' || raw === 'canceled') return raw;
  return 'unknown';
};

const statusLabel = (status: PurchaseLogStatus) => {
  if (status === 'attempted') return 'Attempted';
  if (status === 'success') return 'Success';
  if (status === 'failed') return 'Failed';
  if (status === 'canceled') return 'Canceled';
  return 'Unknown';
};

const statusTone = (status: PurchaseLogStatus) => {
  if (status === 'success') return 'bg-green-50 border-green-200 text-green-700';
  if (status === 'failed') return 'bg-red-50 border-red-200 text-red-700';
  if (status === 'canceled') return 'bg-orange-50 border-orange-200 text-orange-700';
  if (status === 'attempted') return 'bg-blue-50 border-blue-200 text-blue-700';
  return 'bg-neutral-100 border-neutral-200 text-neutral-600';
};

const compactReason = (log: PurchaseLog) =>
  log.cancelReasonLabel ||
  log.cancelReasonCode ||
  log.failureReason ||
  log.readableErrorCode ||
  log.errorDescription ||
  'N/A';

const logTime = (log: PurchaseLog) =>
  formatTimestamp(log.createdAt, log.createdAtEpoch, log.updatedAt);

const searchableText = (log: PurchaseLog) =>
  [
    log.id,
    log.userId,
    log.email,
    log.source,
    normalizeStatus(log),
    planLabel(log),
    log.failureReason,
    log.cancelReasonCode,
    log.cancelReasonLabel,
    log.errorDomain,
    log.errorCode,
    log.readableErrorCode,
    log.errorDescription,
    JSON.stringify(log.plan || {}),
    JSON.stringify(log.metadata || {}),
    JSON.stringify(log.cancelFeedbackMetadata || {}),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const PurchaseLogsAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [logs, setLogs] = useState<PurchaseLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<PurchaseLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchLogs = async () => {
    if (!db) return;
    setLoading(true);
    setError(null);
    try {
      const snapshot = await getDocs(
        query(collection(db, COLLECTION_NAME), orderBy('createdAtEpoch', 'desc'), limit(250))
      );
      const next = snapshot.docs.map((docSnapshot) => ({
        id: docSnapshot.id,
        ...(docSnapshot.data() as Omit<PurchaseLog, 'id'>),
      }));
      setLogs(next);
      setSelectedLog((current) => {
        if (!current) return next[0] || null;
        return next.find((log) => log.id === current.id) || next[0] || null;
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load purchase logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return logs;
    return logs.filter((log) => searchableText(log).includes(needle));
  }, [logs, search]);

  const stats = useMemo(() => {
    const counts = logs.reduce(
      (acc, log) => {
        acc[normalizeStatus(log)] += 1;
        return acc;
      },
      { attempted: 0, success: 0, failed: 0, canceled: 0, unknown: 0 } as Record<PurchaseLogStatus, number>
    );
    const completed = counts.success + counts.failed + counts.canceled;
    const successRate = completed > 0 ? Math.round((counts.success / completed) * 100) : 0;
    return { ...counts, total: logs.length, successRate };
  }, [logs]);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-lg font-bold">Purchase Logs</div>
            <div className="text-xs text-neutral-500">
              Parrot purchase attempts, successes, failures, cancellations, and cancel reasons.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button onClick={signOutUser} className="text-xs text-neutral-600 underline hover:text-neutral-900">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="purchaseLogs" />
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Logs" value={stats.total.toLocaleString()} />
          <StatCard label="Success" value={stats.success.toLocaleString()} />
          <StatCard label="Failed" value={stats.failed.toLocaleString()} />
          <StatCard label="Canceled" value={stats.canceled.toLocaleString()} />
          <StatCard label="Success Rate" value={`${stats.successRate}%`} />
        </div>

        <div className="mb-6 rounded-xl border border-neutral-200 bg-white p-4">
          <label className="block text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">
            Search purchase logs
          </label>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full rounded-lg border border-neutral-200 px-4 py-3 text-sm outline-none focus:border-neutral-900"
            placeholder="Search by email, user ID, status, source, plan, error, or cancel reason"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">Recent Purchases</div>
                <div className="text-xs text-neutral-500 mt-1">
                  Showing {filteredLogs.length.toLocaleString()} of {logs.length.toLocaleString()} entries from {COLLECTION_NAME}.
                </div>
              </div>
              <button
                onClick={fetchLogs}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-xs font-semibold text-white hover:bg-neutral-800"
              >
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-8 text-sm text-neutral-500">Loading purchase logs...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="p-8 text-sm text-neutral-500">No purchase logs found.</div>
            ) : (
              <div className="divide-y divide-neutral-100">
                {filteredLogs.map((log) => {
                  const status = normalizeStatus(log);
                  return (
                    <button
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={`w-full text-left px-6 py-4 transition hover:bg-neutral-50 ${
                        selectedLog?.id === log.id ? 'bg-yellow-50/70' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-semibold text-neutral-900 truncate">
                            {planLabel(log)}
                          </div>
                          <div className="text-sm text-neutral-600 mt-1 truncate">
                            {log.email || log.userId || 'Unknown user'}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-neutral-400">
                            <span>{logTime(log)}</span>
                            {log.source ? <span>{log.source}</span> : null}
                            <span>{planDetail(log)}</span>
                          </div>
                          <div className="text-xs text-neutral-500 mt-2 line-clamp-2">
                            Reason: {compactReason(log)}
                          </div>
                        </div>
                        <span className={`shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(status)}`}>
                          {statusLabel(status)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <aside className="bg-white border border-neutral-200 rounded-xl p-6 h-fit">
            <div className="text-sm font-semibold text-neutral-900 mb-4">Log Details</div>
            {selectedLog ? (
              <div className="space-y-5">
                <div>
                  <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">Status</div>
                  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(normalizeStatus(selectedLog))}`}>
                    {statusLabel(normalizeStatus(selectedLog))}
                  </span>
                </div>
                <Detail label="Created" value={logTime(selectedLog)} />
                <Detail label="Updated" value={formatTimestamp(selectedLog.updatedAt)} />
                <Detail label="Email" value={selectedLog.email || 'N/A'} />
                <Detail label="User ID" value={selectedLog.userId || 'N/A'} />
                <Detail label="Source" value={selectedLog.source || 'N/A'} />
                <Detail label="Plan" value={planLabel(selectedLog)} />
                <Detail label="Plan Detail" value={planDetail(selectedLog)} />
                <Detail label="Failure Reason" value={selectedLog.failureReason || 'N/A'} />
                <Detail label="Cancel Reason" value={selectedLog.cancelReasonLabel || selectedLog.cancelReasonCode || 'N/A'} />
                <Detail label="Error Domain" value={selectedLog.errorDomain || 'N/A'} />
                <Detail label="Error Code" value={valueText(selectedLog.errorCode)} />
                <Detail label="Readable Error" value={valueText(selectedLog.readableErrorCode)} />
                <Detail label="Error Description" value={selectedLog.errorDescription || 'N/A'} multiline />
                <Detail label="Log ID" value={selectedLog.id} />
                <JsonBlock label="Plan Payload" value={selectedLog.plan || {}} />
                <JsonBlock label="Metadata" value={selectedLog.metadata || {}} />
                <JsonBlock label="Cancel Feedback Metadata" value={selectedLog.cancelFeedbackMetadata || {}} />
              </div>
            ) : (
              <div className="text-sm text-neutral-500">Select a purchase log to inspect it.</div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

const StatCard: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold">{label}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

const Detail: React.FC<{ label: string; value: string; multiline?: boolean }> = ({ label, value, multiline }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-1">{label}</div>
    <div className={`text-sm text-neutral-700 ${multiline ? 'whitespace-pre-wrap' : 'break-all'}`}>
      {value}
    </div>
  </div>
);

const JsonBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-neutral-400 font-semibold mb-2">{label}</div>
    <pre className="max-h-56 overflow-auto rounded-lg bg-neutral-950 p-3 text-xs text-neutral-100 whitespace-pre-wrap break-words">
      {JSON.stringify(value, null, 2)}
    </pre>
  </div>
);
